/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { TFunction } from 'react-i18next';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { TherapyStatusRenderer } from './therapy-status-renderer';
import { TherapyBodyRenderer } from './therapy-body-renderer';
import { TherapyLogDetail } from './therapy-log-detail';
import { Button, Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';

/**
 * `ZLogTherapy` type.
 */

type ZLogTherapy = {
  id: string;
  userId: string;
  requestBody: string;
  responseBody: string | undefined;
  responseStatus: string | undefined;
  error: string | undefined;
  createdAt: string;
  updatedAt: string;
};

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<ZLogTherapy> => [
  {
    title: translate('common.labels.id'),
    dataIndex: 'id',
    key: 'id',
    render: item => <IdCopy>{String(item.id)}</IdCopy>
  },
  {
    title: translate('common.labels.userId'),
    dataIndex: 'userId',
    key: 'userId',
    render: item => <IdCopy>{String(item.userId)}</IdCopy>
  },
  {
    title: translate('common.labels.responseStatus'),
    dataIndex: 'responseStatus',
    key: 'responseStatus',
    render: item => <TherapyStatusRenderer status={item.responseStatus || '0'} />
  },
  {
    title: translate('common.labels.body'),
    dataIndex: 'body',
    size: '1fr',
    key: 'body',
    render: item => (
      <TherapyBodyRenderer
        error={item.error || null}
        responseBody={item.responseBody || '{}'}
        responseStatus={item.responseStatus || '0'}
      />
    )
  },
  {
    title: translate('common.labels.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} showMilliseconds />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'hasError',
    inline: true,
    label: translate('common.labels.status'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.success'),
        value: 'false'
      },
      {
        label: translate('common.labels.error'),
        value: 'true'
      }
    ]
  }
];

/**
 * Delete Therapy Logs Button Component.
 */

function DeleteTherapyLogsButton({ refresh }: { refresh: () => Promise<any> }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const client = useRequestProvider();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const url = resolveAppUrl(apiEndpoints.logsTherapyDelete);
      const response = await client.delete(url);
      return response.data;
    },
    onSuccess: async () => {
      await refresh();
      Modal.success({
        title: 'Success',
        content: 'Therapy logs deleted successfully'
      });
      setIsModalVisible(false);
    },
    onError: (error: any) => {
      Modal.error({
        title: 'Error',
        content: error?.response?.data?.message || 'Failed to delete therapy logs'
      });
    }
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  return (
    <>
      <Button
        danger
        icon={<DeleteOutlined />}
        loading={deleteMutation.isLoading}
        onClick={() => setIsModalVisible(true)}
        size={'large'}
      >
        {'Delete Therapy Logs (All)'}
      </Button>
      <Modal
        cancelText={'Cancel'}
        okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
        okText={'Delete'}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleDelete}
        open={isModalVisible}
        title={'Confirm Delete'}
      >
        <p>{'Are you sure you want to delete ALL therapy logs? This action cannot be undone.'}</p>
      </Modal>
    </>
  );
}

/**
 * List.
 */

function list(translate: TFunction): CrudListType<ZLogTherapy> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.developer.logsTherapy,
    enableExport: true,
    canEdit: false,
    canRemove: false,
    extraActions: ({ refresh }) => <DeleteTherapyLogsButton refresh={refresh} />,
    resolveParams: (params: any, item?: ZLogTherapy) => {
      return {
        id: params.id ?? item?.id
      };
    },
    key: ['logs-therapy'],
    defaultQueryString: 'page=1&pageSize=25',
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters || {};

      return {
        filters: pickBy(filters, identity),
        order: data.order && data.order.length > 0 ? data.order : [{ key: 'createdAt', direction: 'desc' }]
      };
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 50,
      pageSizeOptions: [10, 25, 50, 100, 250],
      pagingByTime: true,
      timeField: 'createdAt'
    },
    clickLine: (item: ZLogTherapy) => {
      const div = document.createElement('div');
      div.id = 'therapy-log-detail-drawer';
      document.body.appendChild(div);

      const root = ReactDOM.createRoot(div);

      const handleClose = () => {
        root.unmount();
        document.body.removeChild(div);
      };

      root.render(<TherapyLogDetail item={item} onClose={handleClose} />);
    },
    endpoint: apiEndpoints.logsTherapy,
    endpointById: apiEndpoints.logsTherapyById
  };
}

/**
 * Config.
 */

export function createLogsTherapyTemplate(translate: TFunction): CrudTemplate<ZLogTherapy> {
  return {
    list: list(translate)
  };
}
